import type {
    ComposableOptionsBase
} from '~/types';
import type {
    QueryParamsExtended,
    CustomPaginationBase,
    ApiQueryParams,
    PaginationParams
} from 'x1-base/types';
import { clone as lo_clone } from 'es-toolkit';
import { snakeCase as lo_snakeCase } from 'es-toolkit';
import { merge as lo_mergeCompat } from 'es-toolkit/compat';
import { omit as lo_omit } from 'es-toolkit';
import { omitBy as lo_omitBy } from 'es-toolkit';
import { isNil as lo_isNil } from 'es-toolkit';
import { camelCase as lo_camelCase } from 'es-toolkit';
import { createEventHook, pausableFilter, watchWithFilter } from '@vueuse/core';
import type { DataTableSortItem } from 'vuetify';


const defaultPageSize = 10;
const objPaginationRequest: PaginationParams = {
    page_size: defaultPageSize,
    page: 1
};
const objPagination: CustomPaginationBase = {
    all: false,
    count: 0,
    totalPages: 0,
    pageSize: defaultPageSize,
    page: 1,
    next: '',
    previous: '',
    hasNext: false,
    hasPrevious: false
};


export interface UseApiQueryParamsOptions extends ComposableOptionsBase {
    key: string;
    resetOnUnload: boolean;
    opts: Partial<ApiQueryParams<PaginationParams>>;
    persistedParams: Partial<ApiQueryParams<PaginationParams>>;
    sortProp: Nilish<string>;
}
export function useApiQueryParams(
    options: Partial<UseApiQueryParamsOptions> = {
        resetOnUnload: false,
        opts: {},
        persistedParams: {},
        sortProp: null
    }
) {
    const {
        key,
        resetOnUnload,
        opts,
        persistedParams,
        sortProp = 'ordering'
    } = options;


    const keyPaginationRequest = `keyPaginationRequest_${key}`;
    const keyPagination = `keyPagination_${key}`;
    const keyApiQueryParams = `keyApiQueryParams_${key}`;

    const queryParamsRequest = useState<QueryParamsExtended>(keyApiQueryParams, () => ({}));
    const paginationRequest = useState<PaginationParams>(keyPaginationRequest, () => lo_mergeCompat({}, objPaginationRequest, opts));
    const pagination = useState<CustomPaginationBase>(keyPagination, () => lo_clone(objPagination));

    const hasQueryParamValues = computed<boolean>(() => Object.values(queryParamsRequest.value).some((val) => !lo_isNil(val)));
    const requestParamsUpdate = createEventHook<
        [
            [Nilish<QueryParamsExtended>, Nilish<PaginationParams>],
            [Nilish<QueryParamsExtended>, Nilish<PaginationParams>]
        ]
    >();
    const {
        eventFilter,
        pause,
        resume
    } = pausableFilter();

    const sortItems = ref<Nullable<DataTableSortItem[]>>(getSortItems());

    // Start paused (should be an option)
    pause();

    function initFromQueryParams(
        params: QueryParamsExtended
    ): void {
        const pageSize = Number(params.page_size ?? params.pageSize ?? objPaginationRequest.page_size);
        const page = Number(params.page ?? objPaginationRequest.page);
        const otherParams = lo_omit(params, ['page', 'page_size', 'pageSize']);

        const pageObj: Partial<CustomPaginationBase> = {
            pageSize,
            page
        };

        updatePagination(pageObj);
        Object.assign(queryParamsRequest.value, otherParams);
        sortItems.value = getSortItems();
    }

    function updatePagination(pageObj: Partial<CustomPaginationBase>): void {
        pagination.value = Object.assign(pagination.value, pageObj);
        paginationRequest.value = {
            page_size: pagination.value.pageSize,
            page: pagination.value.page
        };
    }

    function updateOrderingParam(opts: Nilish<{key: string, order: string}>): void {
        if (!opts) {
            delete queryParamsRequest.value?.[sortProp!];
            sortItems.value = null;
            return;
        }

        const { key, order } = opts;
        const paramName = lo_snakeCase(key || '');

        queryParamsRequest.value.ordering = `${order === 'desc' ? '-' : ''}${paramName}`;
        sortItems.value = getSortItems();
    }

    function getApiQueryParams(): ApiQueryParams {
        const params = queryParamsRequest.value ?? {};
        const qParams = lo_omitBy(params, lo_isNil);
        return lo_mergeCompat({}, paginationRequest.value, qParams, persistedParams);
    }

    function clearApiQueryParams(): void {
        const keys = Object.keys(queryParamsRequest.value) ?? [];
        keys.forEach((key) => queryParamsRequest.value[key] = null);
    }

    function getSortItems(): Nullable<DataTableSortItem[]> {
        let ordering = queryParamsRequest.value?.[sortProp!] as Nilish<string>;

        if (!ordering) return null;

        const order = ordering.startsWith('-') ? 'desc' : 'asc';
        const key = lo_camelCase(ordering);

        return [{
            key,
            order
        }];
    }

    function clear() {
        queryParamsRequest.value = {};
        paginationRequest.value = lo_mergeCompat({}, objPaginationRequest, opts);
        pagination.value = lo_clone(objPagination);
        sortItems.value = null;
    }

    function reset(): void {
        clearNuxtState([
            keyPaginationRequest,
            keyPagination,
            keyApiQueryParams
        ]);
    }

    const unWatchRequestParams = watchWithFilter(
        [
            queryParamsRequest,
            paginationRequest
        ],
        requestParamsUpdate.trigger,
        {
            deep: true,
            eventFilter
        },
    );

    onBeforeUnmount(() => {
        unWatchRequestParams();

        if (resetOnUnload) {
            reset();
        }
    });

    return {
        initFromQueryParams,
        getApiQueryParams,
        paginationRequest,
        pagination,
        updatePagination,
        updateOrderingParam,
        queryParamsRequest,
        sortItems,
        onRequestParamsUpdate: requestParamsUpdate.on,
        requestParamsWatcherPause: pause,
        requestParamsWatcherResume: resume,
        hasQueryParamValues,
        clearApiQueryParams,
        clear,
        reset
    };
}
